import type { FC, ReactElement } from 'react';

import { Collapsible as PrimitiveCollapsible } from '@base-ui/react/collapsible';

import type { ClassNameProps } from '@tripleten/cn';

import { requireSingleReactElement } from '../../../utils/require-single-react-element.ts';
import { cnCollapsible } from '../collapsible.constants.ts';
import { useClipped } from '../collapsible.hooks/use-clipped.ts';

/**
 * Props for the Collapsible.Trigger component. Expects a single React element as child.
 */
export type CollapsibleTriggerProps = ClassNameProps &
    Omit<PrimitiveCollapsible.Trigger.Props, 'children' | 'render'> & {
        children: ReactElement;
    };

/**
 * The button that toggles the collapsible panel. Must wrap a single React element. Pass
 * `nativeButton={false}` when that element is not a `<button>`.
 *
 * Hidden while a `partial` panel clips nothing.
 *
 * ```tsx
 * <Collapsible.Trigger>
 *     <Button>Toggle</Button>
 * </Collapsible.Trigger>
 * ```
 */
export const CollapsibleTrigger: FC<CollapsibleTriggerProps> = ({ children, className, ...props }) => {
    const clipped = useClipped();

    if (clipped === false) return undefined;

    return (
        <PrimitiveCollapsible.Trigger
            {...props}
            className={cnCollapsible('trigger', [className])}
            render={requireSingleReactElement(children)}
        />
    );
};
